import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";
import Header from "./Header";

const BASE_URL =
  "https://522gqh3w18.execute-api.ap-south-1.amazonaws.com/Prod/";

function AllMarketHolidays() {
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(new Set()); // Set of composite keys "date||exchange"
  const [deleting, setDeleting] = useState(false);

  // ── Filter & Pagination state ─────────────────────────────────────────────
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // ── Fetch ─────────────────────────────────────────────────────────────────
  const fetchHolidays = async () => {
    setLoading(true);
    setError(null);
    setSelected(new Set());
    try {
      const res = await fetch(`${BASE_URL}/GetAllSymbolsOrHolidays?type=holiday`, {
        headers: { Authorization: localStorage.getItem("SLNAdminToken") ?? "" },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}: ${res.statusText}`);
      const data = await res.json();
      console.log("📦 GetAllMarketHolidays response:", data);
      const list = Array.isArray(data)
        ? data
        : (data.holidays ?? data.data ?? []);
      // Sort ascending by date
      list.sort((a, b) => new Date(a.date) - new Date(b.date));
      setHolidays(list);
    } catch (err) {
      console.error("❌ Error fetching holidays:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHolidays();
  }, []);

  // ── Key helper ────────────────────────────────────────────────────────────
  const rowKey = (h) => `${h.date}||${h.exchange}`;

  // ── Checkbox helpers ──────────────────────────────────────────────────────
  const toggleOne = (key) =>
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });

  const toggleAll = () => {
    if (selected.size === holidays.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(holidays.map(rowKey)));
    }
  };

  const allChecked = holidays.length > 0 && selected.size === holidays.length;
  const someChecked = selected.size > 0 && !allChecked;

  // ── Build delete payload from selected keys ───────────────────────────────
  const buildDeletePayload = (keys) =>
    holidays
      .filter((h) => keys.has(rowKey(h)))
      .map(({ date, exchange }) => ({ date, exchange }));

  // ── Delete selected ───────────────────────────────────────────────────────
  const handleDeleteSelected = async () => {
    const count = selected.size;
    const result = await Swal.fire({
      title: `Delete ${count} Holiday${count > 1 ? "s" : ""}?`,
      html: `Are you sure you want to delete <strong>${count}</strong> selected holiday${count > 1 ? "s" : ""}?<br/><span style="color:#888;font-size:13px">This action cannot be undone.</span>`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc3545",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, Delete",
      cancelButtonText: "Cancel",
      reverseButtons: true,
      customClass: {
        popup: "rounded-4",
        confirmButton: "px-4 rounded-3",
        cancelButton: "px-4 rounded-3",
      },
    });

    if (!result.isConfirmed) return;

    const payload = buildDeletePayload(selected);
    setDeleting(true);
    console.log("📤 DeleteMarketHolidays payload:", payload);

    try {
      const res = await fetch(`${BASE_URL}/DeleteMarketHolidays`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: localStorage.getItem("SLNAdminToken") ?? "",
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json().catch(() => ({}));
      console.log("🗑️ DeleteMarketHolidays response:", data);
      if (!res.ok) throw new Error(data?.message ?? `HTTP ${res.status}`);

      setHolidays((prev) => prev.filter((h) => !selected.has(rowKey(h))));
      setSelected(new Set());

      await Swal.fire({
        title: "Deleted!",
        text: `${count} holiday${count > 1 ? "s" : ""} removed successfully.`,
        icon: "success",
        timer: 2000,
        showConfirmButton: false,
        customClass: { popup: "rounded-4" },
      });
    } catch (err) {
      console.error("❌ DeleteMarketHolidays error:", err);
      await Swal.fire({
        title: "Delete Failed",
        text: err.message || "Something went wrong. Please try again.",
        icon: "error",
        confirmButtonColor: "#dc3545",
        customClass: { popup: "rounded-4", confirmButton: "px-4 rounded-3" },
      });
    } finally {
      setDeleting(false);
    }
  };

  // ── Delete single ─────────────────────────────────────────────────────────
  const handleDeleteOne = async (holiday) => {
    const key = rowKey(holiday);
    const result = await Swal.fire({
      title: "Delete Holiday?",
      html: `Delete <strong>${holiday.reason ?? holiday.date}</strong> on <strong>${formatDate(holiday.date)}</strong> (${holiday.exchange})?<br/><span style="color:#888;font-size:13px">This action cannot be undone.</span>`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc3545",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, Delete",
      cancelButtonText: "Cancel",
      reverseButtons: true,
      customClass: {
        popup: "rounded-4",
        confirmButton: "px-4 rounded-3",
        cancelButton: "px-4 rounded-3",
      },
    });

    if (!result.isConfirmed) return;

    const payload = [{ date: holiday.date, exchange: holiday.exchange }];
    setDeleting(true);
    console.log("📤 DeleteMarketHolidays (single) payload:", payload);

    try {
      const res = await fetch(`${BASE_URL}/DeleteMarketHolidays`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: localStorage.getItem("SLNAdminToken") ?? "",
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json().catch(() => ({}));
      console.log("🗑️ DeleteMarketHolidays response:", data);
      if (!res.ok) throw new Error(data?.message ?? `HTTP ${res.status}`);

      setHolidays((prev) => prev.filter((h) => rowKey(h) !== key));
      setSelected((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });

      await Swal.fire({
        title: "Deleted!",
        text: "Holiday removed successfully.",
        icon: "success",
        timer: 2000,
        showConfirmButton: false,
        customClass: { popup: "rounded-4" },
      });
    } catch (err) {
      console.error("❌ DeleteMarketHolidays error:", err);
      await Swal.fire({
        title: "Delete Failed",
        text: err.message || "Something went wrong. Please try again.",
        icon: "error",
        confirmButtonColor: "#dc3545",
        customClass: { popup: "rounded-4", confirmButton: "px-4 rounded-3" },
      });
    } finally {
      setDeleting(false);
    }
  };

  // ── Helpers ───────────────────────────────────────────────────────────────
  const formatDate = (raw) => {
    if (!raw) return "—";
    const d = new Date(raw);
    if (isNaN(d)) return raw;
    return d.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const getDayName = (raw) => {
    if (!raw) return "";
    const d = new Date(raw);
    if (isNaN(d)) return "";
    return d.toLocaleDateString("en-IN", { weekday: "long" });
  };

  const isPast = (raw) => raw && new Date(raw) < new Date();
  const isToday = (raw) => {
    if (!raw) return false;
    const d = new Date(raw);
    const t = new Date();
    return (
      d.getDate() === t.getDate() &&
      d.getMonth() === t.getMonth() &&
      d.getFullYear() === t.getFullYear()
    );
  };

  const exchangeBadge = (exchange) => {
    const map = {
      NSE: "bg-primary-subtle text-primary border-primary-subtle",
      BSE: "bg-info-subtle text-info border-info-subtle",
      MCX: "bg-warning-subtle text-warning border-warning-subtle",
      NFO: "bg-purple-subtle text-dark border-secondary-subtle",
      BOTH: "bg-success-subtle text-success border-success-subtle",
    };
    return (
      map[exchange?.toUpperCase()] ??
      "bg-secondary-subtle text-secondary border-secondary-subtle"
    );
  };

  // Group upcoming vs past for quick stats
  const upcomingCount = holidays.filter((h) => !isPast(h.date)).length;
  const pastCount = holidays.filter((h) => isPast(h.date)).length;

  // ── Filter & Pagination derived data ──────────────────────────────────────
  const filteredHolidays = holidays.filter((h) => {
    if (!searchTerm.trim()) return true;
    const q = searchTerm.toLowerCase();
    return (
      String(h.date ?? "").toLowerCase().includes(q) ||
      String(h.exchange ?? "").toLowerCase().includes(q) ||
      String(h.reason ?? "").toLowerCase().includes(q) ||
      String(h.description ?? "").toLowerCase().includes(q) ||
      formatDate(h.date).toLowerCase().includes(q) ||
      getDayName(h.date).toLowerCase().includes(q)
    );
  });

  const totalPages = Math.max(1, Math.ceil(filteredHolidays.length / pageSize));
  const safePage = Math.min(currentPage, totalPages);
  const startIdx = (safePage - 1) * pageSize;
  const paginatedHolidays = filteredHolidays.slice(
    startIdx,
    startIdx + pageSize,
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, pageSize]);

  const getPageNumbers = () => {
    const pages = [];
    const maxButtons = 5;
    let start = Math.max(1, safePage - Math.floor(maxButtons / 2));
    let end = Math.min(totalPages, start + maxButtons - 1);
    if (end - start + 1 < maxButtons) {
      start = Math.max(1, end - maxButtons + 1);
    }
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      <Header />

      <section>
        <div className="container py-3">
          <div className="row">
            <div className="col-12">
              <div className="pb-3 d-flex flex-column flex-md-row gap-3 align-items-md-center justify-content-between">
                <div className="d-flex flex-column">
                  <h4 className="mb-0 lh-1 fw-medium text-dark">
                    Market Holidays{" "}
                    {!loading && !error && (
                      <span className="text-secondary">
                        ({holidays.length})
                      </span>
                    )}
                  </h4>
                  <p className="m-0 lh-sm text-secondary ls-sm">
                    NSE / BSE / MCX Exchange Holidays
                  </p>
                </div>
                <div className="d-flex gap-2 align-items-center">
                  {selected.size > 0 && (
                    <button
                      className="btn btn-danger fw-normal px-4 py-1 rounded-3 d-inline-flex align-items-center gap-2"
                      onClick={handleDeleteSelected}
                      disabled={deleting}
                    >
                      {deleting ? (
                        <span className="spinner-border spinner-border-sm" role="status" />
                      ) : (
                        <i className="bi bi-trash"></i>
                      )}
                      Delete ({selected.size})
                    </button>
                  )}
                  <Link
                    to="/manage/add-market-holiday"
                    className="btn btn-primary fw-normal px-5 py-1 rounded-3"
                  >
                    Add Holidays
                  </Link>
                </div>
              </div>
            </div>
          </div>

          {/* ── Quick Stats ── */}
          {!loading && !error && holidays.length > 0 && (
            <div className="row g-2 mb-3">
              {[
                { label: "Total", value: holidays.length, icon: "bi-calendar3", color: "text-primary" },
                { label: "Upcoming", value: upcomingCount, icon: "bi-calendar-event", color: "text-success" },
                { label: "Past", value: pastCount, icon: "bi-calendar-check", color: "text-secondary" },
              ].map(({ label, value, icon, color }) => (
                <div className="col-lg-1 col-md-2 col-3" key={label}>
                  <div className="bg-white border rounded-3 p-3 text-center">
                    <i className={`bi ${icon} fs-5 ${color} d-block mb-1`}></i>
                    <div className={`fw-semibold fs-5 ${color}`}>{value}</div>
                    <div className="text-secondary small">{label}</div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* ── Loading ── */}
          {loading && (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading…</span>
              </div>
              <p className="mt-2 text-secondary">Fetching market holidays…</p>
            </div>
          )}

          {/* ── Error ── */}
          {!loading && error && (
            <div className="alert alert-danger rounded-3 d-flex align-items-center gap-2">
              <i className="bi bi-exclamation-triangle-fill fs-5"></i>
              <div>
                <strong>Failed to load holidays.</strong> {error}
                <button
                  className="btn btn-sm btn-outline-danger ms-3 rounded-3"
                  onClick={fetchHolidays}
                >
                  Retry
                </button>
              </div>
            </div>
          )}

          {/* ── Search & Page Size ── */}
          {!loading && !error && (
            <div className="row g-2 align-items-center mb-3">
              <div className="col-md-6 col-12">
                <div className="input-group">
                  <span className="input-group-text bg-white">
                    <i className="bi bi-search"></i>
                  </span>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search by date, exchange, reason, day..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  {searchTerm && (
                    <button
                      className="btn btn-outline-secondary"
                      type="button"
                      onClick={() => setSearchTerm("")}
                      title="Clear"
                    >
                      <i className="bi bi-x-lg"></i>
                    </button>
                  )}
                </div>
              </div>
              <div className="col-md-3 col-6">
                <select
                  className="form-select"
                  value={pageSize}
                  onChange={(e) => setPageSize(Number(e.target.value))}
                >
                  <option value={5}>5 per page</option>
                  <option value={10}>10 per page</option>
                  <option value={25}>25 per page</option>
                  <option value={50}>50 per page</option>
                  <option value={100}>100 per page</option>
                </select>
              </div>
              <div className="col-md-3 col-6 text-md-end text-secondary small">
                Showing {filteredHolidays.length === 0 ? 0 : startIdx + 1}–
                {Math.min(startIdx + pageSize, filteredHolidays.length)} of{" "}
                {filteredHolidays.length}
              </div>
            </div>
          )}

          {/* ── Table ── */}
          {!loading && !error && (
            <div className="table-responsive border-0 overflow-y-hidden bg-white rounded-4 mb-6">
              <table className="table mb-0 text-nowrap table-centered">
                <thead className="table-light">
                  <tr>
                    <th className="py-2" style={{ width: "4%" }}>
                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={allChecked}
                        ref={(el) => { if (el) el.indeterminate = someChecked; }}
                        onChange={toggleAll}
                        title="Select all"
                        style={{ cursor: "pointer" }}
                      />
                    </th>
                    <th className="py-2" style={{ width: "4%" }}>No.</th>
                    <th className="lh-1 py-2" style={{ width: "40%" }}>
                      Holiday Details
                      <i className="bi bi-calendar-x ms-2 text-dark"></i>
                      <br />
                      <span className="fs-8 fw-normal text-secondary">
                        Date, Day &amp; Reason
                      </span>
                    </th>
                    <th className="lh-1 py-2" style={{ width: "20%" }}>
                      Exchange
                      <i className="bi bi-building ms-2 text-dark"></i>
                      <br />
                      <span className="fs-8 fw-normal text-secondary">
                        Market Exchange
                      </span>
                    </th>
                    <th className="lh-1 py-2" style={{ width: "16%" }}>
                      Status
                      <i className="bi bi-clock ms-2 text-dark"></i>
                      <br />
                      <span className="fs-8 fw-normal text-secondary">
                        Upcoming / Past
                      </span>
                    </th>
                    <th className="lh-1 py-2" style={{ width: "16%" }}>
                      Action
                      <i className="bi bi-person-gear ms-2 text-dark"></i>
                      <br />
                      <span className="fs-8 fw-normal text-secondary">
                        Role-Specific Action Panel
                      </span>
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {filteredHolidays.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="text-center py-5 text-secondary">
                        <i className="bi bi-calendar-x fs-2 d-block mb-2"></i>
                        {holidays.length === 0
                          ? "No market holidays found."
                          : "No holidays match your search."}
                      </td>
                    </tr>
                  ) : (
                    paginatedHolidays.map((holiday, idx) => {
                      const key      = rowKey(holiday);
                      const date     = holiday.date;
                      const exchange = holiday.exchange ?? "—";
                      const reason   = holiday.reason ?? holiday.description ?? "—";
                      const isChecked = selected.has(key);
                      const past     = isPast(date);
                      const today    = isToday(date);

                      return (
                        <tr
                          key={key}
                          style={{
                            opacity: deleting && isChecked ? 0.5 : 1,
                            background: today
                              ? "#fffbeb"
                              : isChecked
                              ? "#f8f9ff"
                              : undefined,
                          }}
                        >
                          {/* Checkbox */}
                          <td>
                            <input
                              type="checkbox"
                              className="form-check-input"
                              checked={isChecked}
                              onChange={() => toggleOne(key)}
                              style={{ cursor: "pointer" }}
                            />
                          </td>

                          <td>{startIdx + idx + 1}.</td>

                          {/* Holiday Details */}
                          <td className="lh-sm py-4">
                            <div className="d-flex align-items-center gap-2 mb-1">
                              <span className="fw-semibold text-dark">
                                {reason}
                              </span>
                              {today && (
                                <span className="badge bg-warning text-dark px-2 py-1 rounded-3 small">
                                  Today
                                </span>
                              )}
                            </div>
                            <span className="text-dark">
                              <strong>Date:</strong>
                            </span>{" "}
                            {formatDate(date)}
                            <br />
                            <span className="text-dark">
                              <strong>Day:</strong>
                            </span>{" "}
                            <span className="text-secondary">
                              {getDayName(date)}
                            </span>
                          </td>

                          {/* Exchange */}
                          <td>
                            <span
                              className={`badge border px-3 py-1 rounded-3 fw-semibold ${exchangeBadge(exchange)}`}
                            >
                              {exchange}
                            </span>
                          </td>

                          {/* Status */}
                          <td>
                            {today ? (
                              <span className="badge bg-warning text-dark border border-warning-subtle px-2 py-1 rounded-3 d-inline-flex align-items-center gap-1">
                                <i className="bi bi-star-fill"></i> Today
                              </span>
                            ) : past ? (
                              <span className="badge bg-secondary-subtle text-secondary border border-secondary-subtle px-2 py-1 rounded-3 d-inline-flex align-items-center gap-1">
                                <i className="bi bi-check-circle-fill"></i> Past
                              </span>
                            ) : (
                              <span className="badge bg-success-subtle text-success border border-success-subtle px-2 py-1 rounded-3 d-inline-flex align-items-center gap-1">
                                <i className="bi bi-calendar-event-fill"></i> Upcoming
                              </span>
                            )}
                          </td>

                          {/* Action */}
                          <td>
                            <div className="d-flex gap-1 align-items-start">
                              <button
                                className="btn p-0 border-0 bg-transparent"
                                title="Delete"
                                disabled={deleting}
                                onClick={() => handleDeleteOne(holiday)}
                              >
                                <div className="icon-shape icon-md bg-light-danger rounded-circle">
                                  <i className="bi bi-trash fs-4 text-dark"></i>
                                </div>
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* ── Pagination ── */}
          {!loading && !error && filteredHolidays.length > 0 && totalPages > 1 && (
            <nav aria-label="Holidays pagination" className="mt-3">
              <ul className="pagination justify-content-center mb-0 flex-wrap">
                <li className={`page-item ${safePage === 1 ? "disabled" : ""}`}>
                  <button
                    className="page-link"
                    onClick={() => setCurrentPage(1)}
                    disabled={safePage === 1}
                  >
                    <i className="bi bi-chevron-double-left"></i>
                  </button>
                </li>
                <li className={`page-item ${safePage === 1 ? "disabled" : ""}`}>
                  <button
                    className="page-link"
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={safePage === 1}
                  >
                    <i className="bi bi-chevron-left"></i>
                  </button>
                </li>
                {getPageNumbers().map((num) => (
                  <li
                    key={num}
                    className={`page-item ${num === safePage ? "active" : ""}`}
                  >
                    <button
                      className="page-link"
                      onClick={() => setCurrentPage(num)}
                    >
                      {num}
                    </button>
                  </li>
                ))}
                <li
                  className={`page-item ${safePage === totalPages ? "disabled" : ""}`}
                >
                  <button
                    className="page-link"
                    onClick={() =>
                      setCurrentPage((p) => Math.min(totalPages, p + 1))
                    }
                    disabled={safePage === totalPages}
                  >
                    <i className="bi bi-chevron-right"></i>
                  </button>
                </li>
                <li
                  className={`page-item ${safePage === totalPages ? "disabled" : ""}`}
                >
                  <button
                    className="page-link"
                    onClick={() => setCurrentPage(totalPages)}
                    disabled={safePage === totalPages}
                  >
                    <i className="bi bi-chevron-double-right"></i>
                  </button>
                </li>
              </ul>
            </nav>
          )}
        </div>
      </section>
    </>
  );
}

export default AllMarketHolidays;
